import { useState } from "react";

// Third Party
import { useQueries, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

// UI
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Download, FileText, ExternalLink, Eye } from "lucide-react";

// Utils
import { cn } from "@/lib/utils";
import useUserToken from "@/hook/useUserToken";
import formatDate from "@/utils/formatDate";

// Services and Types
import { getDocumentStatus, getDocumentStatusRead, acceptDocument } from "@/services/chat";
import type { FileStatus, Reader } from "@/types/chat";
import { CHAT_BUCKET_LINK } from "@/components/constants";
import { Spinner } from "@/components/ui/spinner";
import calculatePassedTime from "@/utils/calculatePassedTime";

export const Route = createFileRoute(
	"/_layout-private/chat/_layout/group/$id/document",
)({
	component: RouteComponent,
	validateSearch: (search) => ({
		conversationName: search.conversationName as string | undefined,
		conversationImage: search.conversationImage as string | undefined,
	}),
});

type TabType = "ACCEPTED" | "PENDING";

const tabs = [
	{
		title: "ເອກະສານທີ່ຕອບຮັບແລ້ວ",
		value: "ACCEPTED",
	},
	{
		title: "ເອກະສານທີ່ລໍຖ້າການຕອບຮັບ",
		value: "PENDING",
	},
];

function RouteComponent() {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const { id } = Route.useParams();
	const { conversationImage, conversationName } = Route.useSearch();
	const user = useUserToken();

	// States
	const [activeTab, setActiveTab] = useState<TabType>("PENDING");
	const [selectedFile, setSelectedFile] = useState("")
	const [readModal, setReadModal] = useState(false);


	// Queries
	const [
		{ data: acceptRes, isLoading: acceptLoading },
		{ data: respondedRes, isLoading: respondedLoading },
	] = useQueries({
		queries: [
			{
				queryKey: ["document-status", "ACCEPTED", id],
				queryFn: () =>
					getDocumentStatus({
						userId: user?.data?.userChatId,
						conversationId: id,
						fileStatus: "ACCEPTED",
					}),
			},
			{
				queryKey: ["document-status", "PENDING", id],
				queryFn: () =>
					getDocumentStatus({
						userId: user?.data?.userChatId,
						conversationId: id,
						fileStatus: "PENDING",
					}),
			},
		],
	});

	// Mutation
	const acceptDocumentMutation = useMutation({
		mutationFn: (messageId: string) => acceptDocument({
			userId: user?.data?.userChatId,
			messageId: messageId
		}),
		onSuccess: (data) => {
			console.log("Success", data);

			queryClient.invalidateQueries({ queryKey: ["document-status", "ACCEPTED", id] });
			queryClient.invalidateQueries({ queryKey: ["document-status", "PENDING", id] });


			toast({
				title: "ສຳເລັດ",
				description: "ຮັບເອກະສານສຳເລັດ",
			});
		},
		onError: (error) => {
			console.log("Error", error);

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive"
			});
		},
	});

	const { data: readRes, isLoading: readLoading } = useQuery({
		queryKey: ["document-status-read", selectedFile, id],
		queryFn: () =>
			getDocumentStatusRead({
				userId: user?.data?.userChatId,
				messageId: selectedFile,
			}),
		enabled: !!selectedFile,
	})

	const isLoading = acceptLoading || respondedLoading;

	if (isLoading) {
		return <Skeleton className="w-full h-[calc(100vh-200px)]" />;
	}

	const acceptData = acceptRes?.data as FileStatus[];
	const respondedData = respondedRes?.data as FileStatus[];
	const readData = readRes?.data as Reader[];

	console.log(respondedData, "Read Data")

	return (
		<div className="h-full overflow-y-auto max-h-[calc(100vh-200px)]">
			<header className="flex relative justify-center items-center p-4 border-b">
				<button
					onClick={() => window.history.back()}
					className="flex absolute left-4 items-center text-sm transition-colors hover:text-primary"
				>
					<ArrowLeft className="mr-2 w-[16px] h-[16px]" />
					<span>ກັບຄືນ</span>
				</button>

				<h2 className="font-bold text-[18px]">ເອກະສານທັງໝົດ</h2>
			</header>

			<div className="flex flex-col items-center text-sm gap-[24px] p-[28px]">
				<Avatar className="w-[120px] h-[120px]">
					<AvatarImage
						className="object-cover"
						src={
							CHAT_BUCKET_LINK + "/images/" + conversationImage
						}
					/>
					<AvatarFallback>{conversationName?.charAt(0)}</AvatarFallback>
				</Avatar>

				<div className="flex flex-col items-center">
					<h2>{conversationName}</h2>
					<p className="text-muted-foreground">ກຸ່ມສົນທະນາ</p>
				</div>
			</div>

			<div className="w-full px-[28px]">
				<Tabs defaultValue={activeTab} value={activeTab} className="w-full">
					<TabsList className="flex justify-around place-items-center p-0 w-full bg-transparent rounded-none border-b">
						{tabs.map((tab) => (
							<TabsTrigger
								className="data-[state=active]:active-tab-document h-full"
								key={tab.value}
								value={tab.value}
								onClick={() => setActiveTab(tab.value as TabType)}
							>
								{tab.title}{" "}
								<span className="text-red-500">
									(
									{tab.value === "ACCEPTED"
										? acceptData?.length || 0
										: respondedData?.length || 0
									}
									)
								</span>
							</TabsTrigger>
						))}
					</TabsList>
					<TabsContent value="ACCEPTED">
						<div className="space-y-4">
							{acceptData?.map((file) => (
								<div key={file?._id} className="flex justify-start">
									<div className="flex flex-col max-w-[80%] w-full">
										<div className="flex gap-3 items-start p-4 rounded-lg transition-colors bg-muted hover:bg-muted/80">
											<FileText className="w-6 h-6 shrink-0 text-primary" />

											<div className="flex-1 min-w-0">
												<h3 className="font-medium truncate">
													{file?.message?.attachments[0]?.fileName}
												</h3>
												<p className="text-sm text-muted-foreground">
													ຕອບຮັບເມື່ອ: {" "}

													{formatDate({
														date: file?.updatedAt,
														format: "DD/MM/YYYY HH:mm"
													})}
												</p>
											</div>

											<a
												target="_blank"
												rel="noopener noreferrer"
												href={`${CHAT_BUCKET_LINK}/images/${file?.message?.attachments[0]?.fileName}`}
												download
												className="shrink-0"
												onClick={(e) => e.stopPropagation()}
											>
												<Button size="icon" variant="ghost">
													<Download className="w-4 h-4" />
												</Button>
											</a>
										</div>
									</div>
								</div>
							))}
						</div>
					</TabsContent>
					<TabsContent value="PENDING">


						<div className="space-y-4">
							{respondedData?.map((file) => (
								<div key={file?._id} className="flex justify-start">
									<div className="flex flex-col max-w-[80%] w-full">
										<div className="flex gap-3 items-start p-4 rounded-lg transition-colors bg-muted hover:bg-muted/80">
											<FileText className="w-6 h-6 shrink-0 text-primary" />

											<div className="flex-1 min-w-0">
												<h3 className="font-medium truncate">
													{file?.message?.attachments[0]?.fileName}
												</h3>
												<p className="text-sm text-muted-foreground">
													{/* {new Date(file?.message?.sendAt).toLocaleString('lo-LA', {
														year: 'numeric',
														month: 'long',
														day: 'numeric',
														hour: '2-digit',
														minute: '2-digit'
													})} */}

													{/* {formatDate({
														date: file?.message?.sendAt,
														format: "DD/MM/YYYY HH:mm"
													})} */}

													{calculatePassedTime(file?.message?.sendAt)}
												</p>
											</div>

											<a
												target="_blank"
												rel="noopener noreferrer"
												href={`${CHAT_BUCKET_LINK}/images/${file?.message?.attachments[0]?.fileName}`}
												download
												className="shrink-0"
												onClick={(e) => e.stopPropagation()}
											>
												<Button size="icon" variant="ghost">
													<Download className="w-4 h-4" />
												</Button>
											</a>
										</div>
										<Button
											className="mt-2 w-fit"
											variant={"outline"}
											onClick={() => {
												acceptDocumentMutation.mutate(file?.message?._id)
											}}
											disabled={acceptDocumentMutation.isPending && acceptDocumentMutation.variables === file?.message?._id}
										>
											{acceptDocumentMutation.isPending && acceptDocumentMutation.variables === file?.message?._id ? <Spinner /> : "ຕອບຮັບເອກະສານ"}
										</Button>
									</div>

								</div>
							))}

						</div>
					</TabsContent>
				</Tabs>
			</div>
			<Dialog open={readModal} onOpenChange={setReadModal}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>ລາຍຊື່ຜູ້ຕອບຮັບເອກະສານ</DialogTitle>
						<DialogDescription>
							ລາຍຊື່ຜູ້ໃຊ້ທີ່ໄດ້ຕອບຮັບເອກະສານນີ້ແລ້ວ
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 max-h-[400px] overflow-y-auto py-4">
						{readLoading ? (
							<div className="space-y-3">
								<Skeleton className="w-full h-12" />
								<Skeleton className="w-full h-12" />
								<Skeleton className="w-full h-12" />
							</div>
						) : readData?.length > 0 ? (
							readData.map((user) => (
								<div
									key={user?._id}
									className="flex gap-3 items-center p-3 rounded-lg border"
								>
									<Avatar className="w-10 h-10">
										<AvatarImage
											src={`${CHAT_BUCKET_LINK}/images/${user?.receiver?.profileUrl}`}
											className="object-cover"
										/>
										<AvatarFallback>
											{user?.receiver?.fullName?.charAt(0)}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 min-w-0">
										<p className="font-medium truncate">
											{user?.receiver?.fullName}
										</p>
										<p className="text-sm text-muted-foreground">
											ຕອບຮັບເມື່ອ: {" "}

											{formatDate({
												date: user?.updatedAt,
												format: "DD/MM/YYYY HH:mm"
											})}
										</p>
									</div>
								</div>
							))
						) : (
							<p className="text-center text-muted-foreground">
								ຍັງບໍ່ມີຜູ້ອ່ານເອກະສານນີ້
							</p>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setReadModal(false)}>
							ປິດ
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
